import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  PlusIcon,
  Cog6ToothIcon,
  AcademicCapIcon,
  BanknotesIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  UsersIcon,
  BellIcon
} from "@heroicons/react/24/outline";
import { BRAND_LOGO, BRAND_NAME } from "../utils/brandAssets";

const SuperAdminNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Close menus when location changes (user navigates)
  useEffect(() => {
    setShowMoreMenu(false);
    setShowProfileMenu(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreMenu && !event.target.closest('.more-dropdown-container')) {
        setShowMoreMenu(false);
      }
      if (showProfileMenu && !event.target.closest('.profile-dropdown-container')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu, showProfileMenu]);

  const getLinkClassName = (path) =>
    location.pathname === path
      ? "text-cheese font-bold"
      : "text-white hover:text-cheese transition-all duration-300";

  const getMobileLinkClassName = (path) =>
    location.pathname === path
      ? "bg-pepperoni text-white shadow-lg scale-105"
      : "text-white hover:bg-crust/20 transition-all duration-300";

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="bg-crust shadow-lg fixed w-full top-0 z-50 hidden md:block">
        <div className="w-full px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/superadmin/dashboard" className="flex items-center space-x-3 text-white hover:opacity-90 transition-opacity duration-300">
              <motion.img 
                src={BRAND_LOGO} 
                alt="Byte Logo" 
                className="w-10 h-10"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold">{BRAND_NAME}</span>
                <span className="text-xs text-cheese flex items-center">
                  <Cog6ToothIcon className="w-3 h-3 mr-1" />
                  Super Admin Panel
                </span>
              </div>
            </Link>

            <div className="flex items-center space-x-4">
              <Link
                to="/superadmin/dashboard"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${getLinkClassName("/superadmin/dashboard")}`}
              >
                <HomeIcon className="w-5 h-5" />
                <span className="font-medium">Dashboard</span>
              </Link>
              
              <Link
                to="/superadmin/orders"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${getLinkClassName("/superadmin/orders")}`}
              >
                <ClipboardDocumentListIcon className="w-5 h-5" />
                <span className="font-medium">Orders</span>
              </Link>
              
              <Link
                to="/superadmin/restaurants"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${getLinkClassName("/superadmin/restaurants")}`}
              >
                <BuildingStorefrontIcon className="w-5 h-5" />
                <span className="font-medium">Restaurants</span>
              </Link>

              <Link
                to="/superadmin/transactions"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${getLinkClassName("/superadmin/transactions")}`}
              >
                <BanknotesIcon className="w-5 h-5" />
                <span className="font-medium">Transactions</span>
              </Link>
              
              {/* More Menu Dropdown */}
              <div className="relative more-dropdown-container">
                <button
                  onClick={() => {
                    setShowMoreMenu(!showMoreMenu);
                    setShowProfileMenu(false);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 rounded-xl text-white hover:text-cheese transition-all duration-300"
                >
                  <EllipsisHorizontalIcon className="w-5 h-5" />
                  <span className="font-medium">More</span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showMoreMenu ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {showMoreMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 rounded-xl shadow-xl bg-white border border-gray-100 py-2 z-50"
                    >
                      <Link
                        to="/superadmin/discounts"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <PlusIcon className="w-4 h-4 mr-2 text-gray-500" />
                        Discounts
                      </Link>
                      
                      <Link
                        to="/superadmin/university-management"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <AcademicCapIcon className="w-4 h-4 mr-2 text-gray-500" />
                        University Hub
                      </Link>

                      <Link
                        to="/superadmin/withdrawals"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <CurrencyDollarIcon className="w-4 h-4 mr-2 text-gray-500" />
                        Withdrawals
                      </Link>
                      
                      <Link
                        to="/superadmin/users"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <UsersIcon className="w-4 h-4 mr-2 text-gray-500" />
                        Users
                      </Link>
                      
                      <Link
                        to="/superadmin/notifications"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <BellIcon className="w-4 h-4 mr-2 text-gray-500" />
                        Notifications
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <div className="relative profile-dropdown-container">
                <button
                  onClick={() => {
                    setShowProfileMenu(!showProfileMenu);
                    setShowMoreMenu(false);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 rounded-xl text-white hover:text-cheese transition-all duration-300"
                >
                  <UserIcon className="w-5 h-5" />
                  <span className="font-medium">Admin</span>
                </button>
                
                <AnimatePresence>
                  {showProfileMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 rounded-xl shadow-xl bg-white border border-gray-100 py-2 z-50"
                    >
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">Super Admin</p>
                        <p className="text-xs text-gray-500">System Administrator</p>
                      </div>
                      <Link
                        to="/superadmin/settings"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <Cog6ToothIcon className="w-4 h-4 inline mr-2" />
                        Settings
                      </Link>
                      <hr className="my-1" />
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowProfileMenu(false);
                        }}
                        className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 inline mr-2" />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar */}
      <nav className="bg-crust shadow-lg fixed w-full top-0 z-40 flex md:hidden">
        <div className="w-full px-4 py-4">
          <div className="flex items-center">
            <Link
              to="/superadmin/dashboard"
              className="flex items-center space-x-2 text-white"
            >
              <img 
                src={BRAND_LOGO} 
                alt="Byte Logo" 
                className="w-9 h-9"
              />
              <div className="flex flex-col">
                <span className="text-base font-bold">{BRAND_NAME}</span>
                <span className="text-xs text-cheese flex items-center">
                  <Cog6ToothIcon className="w-2 h-2 mr-1" />
                  Admin Panel
                </span>
              </div>
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <motion.nav 
        className="fixed inset-x-0 bottom-0 z-50 bg-crust md:hidden shadow-2xl border-t border-gray-800"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="px-2 py-1">
          <div className="flex justify-around items-center">
            <Link
              to="/superadmin/dashboard"
              className={`flex flex-col items-center justify-center px-3 py-3 rounded-xl transition-all duration-300 ${getMobileLinkClassName("/superadmin/dashboard")}`}
            >
              <HomeIcon className="w-6 h-6 text-current" />
            </Link>
            
            <Link
              to="/superadmin/orders"
              className={`flex flex-col items-center justify-center px-3 py-3 rounded-xl transition-all duration-300 ${getMobileLinkClassName("/superadmin/orders")}`}
            >
              <ClipboardDocumentListIcon className="w-6 h-6 text-current" />
            </Link>

            <Link
              to="/superadmin/restaurants"
              className={`flex flex-col items-center justify-center px-3 py-3 rounded-xl transition-all duration-300 ${getMobileLinkClassName("/superadmin/restaurants")}`}
            >
              <BuildingStorefrontIcon className="w-6 h-6 text-current" />
            </Link>

            <Link
              to="/superadmin/transactions"
              className={`flex flex-col items-center justify-center px-3 py-3 rounded-xl transition-all duration-300 ${getMobileLinkClassName("/superadmin/transactions")}`}
            >
              <BanknotesIcon className="w-6 h-6 text-current" />
            </Link>

            {/* More Menu Button */}
            <div className="relative more-dropdown-container">
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className={`flex flex-col items-center justify-center px-3 py-3 rounded-xl transition-all duration-300 ${location.pathname.includes('/superadmin/discounts') || 
                  location.pathname.includes('/superadmin/university-management') || 
                  location.pathname.includes('/superadmin/withdrawals') || 
                  location.pathname.includes('/superadmin/users') || 
                  location.pathname.includes('/superadmin/notifications') ? 
                  'bg-pepperoni text-white shadow-lg scale-105' : 
                  'text-white hover:bg-crust/20 transition-all duration-300'}`}
              >
                <EllipsisHorizontalIcon className="w-6 h-6 text-current" />
              </button>
              
              <AnimatePresence>
                {showMoreMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -60, scale: 0.95 }}
                    animate={{ opacity: 1, y: -200, scale: 1 }}
                    exit={{ opacity: 0, y: -60, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute bottom-0 right-0 w-56 rounded-xl shadow-xl bg-white border border-gray-100 py-2 z-50"
                  >
                    <Link
                      to="/superadmin/discounts"
                      className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                      onClick={() => setShowMoreMenu(false)}
                    >
                      <PlusIcon className="w-4 h-4 mr-2 text-gray-500" />
                      Discounts
                    </Link>
                    
                    <Link
                      to="/superadmin/university-management"
                      className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                      onClick={() => setShowMoreMenu(false)}
                    >
                      <AcademicCapIcon className="w-4 h-4 mr-2 text-gray-500" />
                      University Hub
                    </Link>

                    <Link
                      to="/superadmin/withdrawals"
                      className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                      onClick={() => setShowMoreMenu(false)}
                    >
                      <CurrencyDollarIcon className="w-4 h-4 mr-2 text-gray-500" />
                      Withdrawals
                    </Link>
                    
                    <Link
                      to="/superadmin/users"
                      className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                      onClick={() => setShowMoreMenu(false)}
                    >
                      <UsersIcon className="w-4 h-4 mr-2 text-gray-500" />
                      Users
                    </Link>
                    
                    <Link
                      to="/superadmin/notifications"
                      className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center"
                      onClick={() => setShowMoreMenu(false)}
                    >
                      <BellIcon className="w-4 h-4 mr-2 text-gray-500" />
                      Notifications
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex flex-col items-center justify-center px-3 py-3 rounded-xl text-white hover:bg-red-600/20 hover:text-red-400 transition-all duration-300"
            >
              <ArrowRightOnRectangleIcon className="w-6 h-6 text-current" />
            </button>
          </div>
        </div>
      </motion.nav>
      
      {/* Content padding to avoid navbar overlap */}
      <div className="pt-12 md:pt-16"></div>
    </>
  );
};

export default SuperAdminNavbar;
